/**
 * Free Product Manager - Moon Moon Agency
 * Auto-adds/removes one or more free products in the cart based on trigger product quantity.
 *
 * Reads static configuration from [data-free-product-configs] rendered by cart-drawer.liquid.
 * Dispatches cart:change (not cart:refresh) to stay in sync with Impact's section rendering.
 * Also broadcasts a "free-product:progress" event consumed by mm_cart_progress_bar.js.
 *
 * Runs even when no "free_product" block is configured, so it can still clean up a free item
 * left over in the cart from a block that was since removed in the theme editor.
 */

/*********************************************************************************************
 * 🎁 FREE PRODUCT MANAGER
 */
const FreeProductManager = {
	_processing: false,
	_configs: [],
	_collectionProductIdsByHandle: new Map(), // Cache for collection product IDs fetched via API
	_dismissedVariantIds: new Set(), // Variant IDs the customer manually removed — re-added only once re-armed

	init() {
		this._loadConfigs();
		this._loadDismissed();
		this._bindEvents();
	},

	_loadDismissed() {
		try {
			const raw = sessionStorage.getItem('mm_free_product_dismissed');
			this._dismissedVariantIds = new Set(raw ? JSON.parse(raw) : []);
		} catch (_) {
			this._dismissedVariantIds = new Set();
		}
	},

	_saveDismissed() {
		try {
			sessionStorage.setItem('mm_free_product_dismissed', JSON.stringify([...this._dismissedVariantIds]));
		} catch (_) {
			// Silent fail
		}
	},

	_loadConfigs() {
		const el = document.querySelector('[data-free-product-configs]');
		if (!el) return;

		try {
			const rawConfigs = JSON.parse(el.textContent);
			this._configs = rawConfigs.map((config) => ({
				variantId: config.variantId,
				productId: config.productId,
				triggerProductIds: config.triggerProductIds ? config.triggerProductIds.split(',') : [],
				triggerCollectionHandle: config.triggerCollectionHandle || '',
				threshold: parseInt(config.threshold) || 1,
			}));
		} catch (_) {
			this._configs = [];
		}
	},

	/**
	 * Fetch all product IDs from a trigger collection via AJAX API (handles >50 products).
	 * Results are cached per collection handle after first fetch.
	 */
	async _fetchCollectionProductIds(handle) {
		if (!handle) return [];
		if (this._collectionProductIdsByHandle.has(handle)) {
			return this._collectionProductIdsByHandle.get(handle);
		}

		const ids = [];
		let page = 1;
		const limit = 250;

		try {
			while (true) {
				const response = await fetch(
					`${Shopify.routes.root}collections/${handle}/products.json?limit=${limit}&page=${page}`
				);
				if (!response.ok) break;
				const data = await response.json();
				if (!data.products || data.products.length === 0) break;
				for (const product of data.products) {
					ids.push(String(product.id));
				}
				if (data.products.length < limit) break;
				page++;
			}
		} catch (_) {
			// Silent fail — will fall back to static list from Liquid
		}

		this._collectionProductIdsByHandle.set(handle, ids);
		return ids;
	},

	_bindEvents() {
		document.addEventListener('cart:change', (e) => {
			this._onCartChanged(e.detail?.cart, e.detail?.baseEvent);
		});

		// Customer explicitly removing a free item is a dismissal, not a state to auto-correct.
		// Recorded on click (not inferred from the cart diff) so it can't race the cart:change handler above.
		document.addEventListener('click', (e) => {
			const link = e.target.closest('[data-free-product-item] [href*="/cart/change"]');
			if (!link) return;
			const variantId = link.closest('[data-free-product-item]')?.dataset.variantId;
			if (!variantId) return;
			this._dismissedVariantIds.add(String(variantId));
			this._saveDismissed();
		});

		// Also check on initial page load (e.g. page refresh with items already in cart)
		this.refresh();
	},

	/**
	 * Fetch the current cart and re-evaluate/re-dispatch progress from scratch.
	 *
	 * Public on purpose: mm_cart_progress_bar.js calls this from connectedCallback. Impact's
	 * cart-drawer replaces .cart-drawer__inner's HTML on every "cart:change" (via a setTimeout,
	 * 0ms for adds / 1250ms otherwise), which destroys and recreates our <mm-cart-progress-bar>
	 * element. Our own "cart:change" handler below usually resolves *before* that swap (its
	 * awaits are cache-hit microtasks), so its "free-product:progress" dispatch reaches the
	 * about-to-be-destroyed instance and gets wiped by the swap moments later. The freshly
	 * recreated instance would otherwise show raw, unsubstituted template text until the next
	 * cart action. Calling refresh() on (re)connect closes that gap.
	 */
	async refresh() {
		try {
			// Pre-fetch collection IDs so the cache is warm before the first cart:change
			await Promise.all(
				this._configs
					.filter((config) => config.triggerCollectionHandle)
					.map((config) => this._fetchCollectionProductIds(config.triggerCollectionHandle))
			);
			const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
			this._evaluateAsync(cart);
		} catch (_) {
			// Silent fail
		}
	},

	_onCartChanged(cartData, baseEvent) {
		if (this._processing || !cartData) return;
		this._evaluateAsync(cartData, baseEvent);
	},

	/**
	 * Core logic: for each configured free product, count trigger products in cart
	 * and decide add/remove. Uses dynamically-fetched collection product IDs to
	 * bypass the 50-product Liquid limit. Also broadcasts progress data so the
	 * tiered progress bar UI can stay in sync without re-fetching collections, and
	 * removes any free item left in the cart whose "free_product" block was deleted
	 * from the section (so it no longer has a matching config).
	 */
	async _evaluateAsync(cartData, baseEvent) {
		if (!cartData?.items || this._processing) return;

		this._processing = true;
		const startedAt = Date.now();

		try {
			let didMutateCart = false;
			const tiers = [];
			const configuredVariantIds = new Set(this._configs.map((config) => String(config.variantId)));

			for (const config of this._configs) {
				const evaluation = await this._evaluateConfig(cartData, config);
				tiers.push({
					variantId: config.variantId,
					productId: config.productId,
					threshold: config.threshold,
					triggerCount: evaluation.triggerCount,
					reached: evaluation.mustAdd,
				});

				const variantIdStr = String(config.variantId);

				if (!evaluation.mustAdd && this._dismissedVariantIds.has(variantIdStr)) {
					// Threshold no longer met — re-arm so a future re-crossing auto-adds again
					this._dismissedVariantIds.delete(variantIdStr);
					this._saveDismissed();
				}

				if (evaluation.mustAdd && !evaluation.freeProductInCart && !this._dismissedVariantIds.has(variantIdStr)) {
					await this._addFreeProduct(config.variantId);
					didMutateCart = true;
				} else if (!evaluation.mustAdd && evaluation.freeProductInCart) {
					await this._removeFreeProduct(evaluation.freeProductLineKey || config.variantId);
					didMutateCart = true;
				}
			}

			for (const item of cartData.items) {
				const isFreeItem = item.properties?._free_product === 'true';
				if (isFreeItem && !configuredVariantIds.has(String(item.variant_id))) {
					await this._removeFreeProduct(item.key);
					didMutateCart = true;
				}
			}

			this._dispatchProgressUpdate(tiers);

			if (didMutateCart) {
				// Impact's own drawer re-render for this cart:change (baseEvent !== "variant:add",
				// e.g. "line-item:change" on quantity/removal) applies its HTML 1250ms after the
				// event fired, using a snapshot of the cart taken *before* our add/remove above.
				// If we dispatch our corrected re-render sooner, that pending stale swap fires
				// afterwards and clobbers it (the free product visually reappears/lingers even
				// though it's already gone server-side). Wait out that window first so our
				// corrected HTML is always the last one applied.
				if (baseEvent !== 'variant:add') {
					const remaining = 1250 - (Date.now() - startedAt);
					if (remaining > 0) {
						await new Promise((resolve) => setTimeout(resolve, remaining + 50));
					}
				}

				await this._dispatchCartUpdate();
			}
		} finally {
			this._processing = false;
		}
	},

	async _evaluateConfig(cartData, config) {
		const { variantId, threshold, triggerCollectionHandle } = config;

		// Resolve the full list: prefer API-fetched collection IDs, fall back to static Liquid list
		let triggerProductIds = triggerCollectionHandle
			? await this._fetchCollectionProductIds(triggerCollectionHandle)
			: [];
		if (triggerProductIds.length === 0) {
			triggerProductIds = config.triggerProductIds;
		}

		let triggerCount = 0;
		let freeProductInCart = false;
		let freeProductLineKey = null;

		for (const item of cartData.items) {
			const isFreeItem = item.properties?._free_product === 'true';

			if (isFreeItem) {
				if (String(item.variant_id) === String(variantId)) {
					freeProductInCart = true;
					freeProductLineKey = item.key;
				}
				continue;
			}

			if (triggerProductIds.includes(String(item.product_id))) {
				triggerCount += item.quantity;
			}
		}

		return {
			triggerCount,
			mustAdd: triggerCount >= threshold,
			freeProductInCart,
			freeProductLineKey,
		};
	},

	/**
	 * Notify the progress bar custom element (if present) of the latest tier states.
	 */
	_dispatchProgressUpdate(tiers) {
		document.dispatchEvent(new CustomEvent('free-product:progress', {
			detail: { tiers },
		}));
	},

	_isCartPage() {
		return window.location.pathname.endsWith('/cart');
	},

	/**
	 * Fetch fresh cart data + section HTML, then dispatch cart:change exactly like Impact does.
	 * This avoids the race condition where cart:refresh + the pending 1250ms setTimeout
	 * from a previous cart:change would overwrite our updated HTML.
	 */
	async _dispatchCartUpdate() {
		if (this._isCartPage()) {
			window.location.reload();
			return;
		}

		try {
			// Fetch cart data and section HTML in parallel
			const sectionId = document.querySelector('#cart-drawer')?.closest('[id^="shopify-section"]')?.id?.replace('shopify-section-', '')
				|| 'cart-drawer';

			const [cartResponse, sectionResponse] = await Promise.all([
				fetch(`${Shopify.routes.root}cart.js`),
				fetch(`${Shopify.routes.root}?section_id=${sectionId}`),
			]);

			const cartData = await cartResponse.json();
			const sectionHtml = await sectionResponse.text();

			// Attach sections data to cart object, matching Impact's format
			cartData.sections = { [sectionId]: sectionHtml };

			// Dispatch cart:change with baseEvent "variant:add" so Impact applies it
			// immediately (0ms delay) instead of waiting 1250ms
			document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
				bubbles: true,
				detail: {
					baseEvent: 'variant:add',
					cart: cartData,
				},
			}));
		} catch (_) {
			// Fallback to cart:refresh if something goes wrong
			document.dispatchEvent(new CustomEvent('cart:refresh'));
		}
	},

	async _addFreeProduct(variantId) {
		try {
			await fetch(`${Shopify.routes.root}cart/add.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					items: [
						{
							id: parseInt(variantId),
							quantity: 1,
							properties: { _free_product: 'true' },
						},
					],
				}),
			});
		} catch (_) {
			// Silent fail — next cart:change will retry
		}
	},

	async _removeFreeProduct(lineKey) {
		try {
			await fetch(`${Shopify.routes.root}cart/change.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					id: lineKey,
					quantity: 0,
				}),
			});
		} catch (_) {
			// Silent fail — next cart:change will retry
		}
	},
};

window.FreeProductManager = FreeProductManager;

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => FreeProductManager.init());
} else {
	FreeProductManager.init();
}
